/** SUNTRONIX ONE — Bulk Deployment (Phase 1 MVP).
 *  Download template → upload → review → confirm.  Batch 360 read-only
 *  view accessible via a link after confirm. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, CheckCircle2, AlertTriangle, XCircle, Rocket, FileArchive } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function BulkDeployment() {
  const nav = useNavigate();
  const { batchId } = useParams();
  const [draft, setDraft] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [batchView, setBatchView] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const fileRef = useRef(null);

  // When a batch id is in the URL, we're viewing an existing batch 360.
  useEffect(() => {
    if (!batchId) { setBatchView(null); return; }
    let cancel = false;
    api.get(`/bulk-deployment/batches/${batchId}`)
      .then((r) => { if (!cancel) setBatchView(r.data); })
      .catch((e) => !cancel && toast.error(e?.response?.data?.detail || "Failed to load batch"));
    return () => { cancel = true; };
  }, [batchId]);

  const upload = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bulk-deployment/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft(data);
      toast.success(`Parsed ${data.counts.total} rows`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  }, []);

  const downloadTemplate = () => {
    // Trigger a full-page download by opening the template URL with cookies.
    window.open(`${API_BASE}/api/bulk-deployment/template`, "_blank");
  };

  const downloadCertificatesZip = useCallback(async (id) => {
    const target = id || batchId;
    if (!target) return;
    setZipping(true);
    try {
      const res = await api.get(`/deployments/batch/${target}/certificates-zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deployment-certificates-${target}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Certificate bundle downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Certificate bundle download failed");
    } finally { setZipping(false); }
  }, [batchId]);

  const confirm = async () => {
    if (!draft) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/bulk-deployment/drafts/${draft.id}/confirm`, {
        accept_inheritance: true,
        accept_conflicts: draft.override_conflicts > 0,
      });
      toast.success(`Batch ${data.batch_id} created · ${data.requests_created} deployment lines`);
      nav(`/bulk-deployment/${data.batch_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed"); }
    finally { setConfirming(false); }
  };

  // ---------- BATCH 360 VIEW (when navigated with :batchId) ----------
  if (batchId) {
    return (
      <div className="max-w-[1400px] mx-auto space-y-6" data-testid="bulk-batch-view">
        <PageHeader
          eyebrow="Operations · Deploy"
          title={`Batch ${batchId}`}
          context="Batch 360 view — download the full certificate bundle to share with the customer."
          actions={
            <div className="flex gap-2">
              <Button variant="create" onClick={() => nav("/bulk-deployment")} data-testid="batch-new-upload">
                <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.6} /> New upload
              </Button>
              <Button onClick={() => downloadCertificatesZip()} disabled={zipping} data-testid="batch-download-zip">
                <FileArchive className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
                {zipping ? "Bundling…" : "Download certificate bundle (.zip)"}
              </Button>
            </div>
          }
          testId="batch-header"
        />
        {!batchView ? (
          <div className="st-panel p-8 text-center text-[13px] text-[color:var(--st-text-muted)]">
            Loading batch…
          </div>
        ) : (
          <>
            <Section title="Batch summary" testId="batch-summary">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Deployment lines" value={batchView.requests?.length || 0} testId="batch-kpi-lines" />
                <Kpi label="Employees" value={batchView.employee_count || 0} testId="batch-kpi-employees" />
                <Kpi label="Exceptions" value={batchView.exceptions?.length || 0} tone={batchView.exceptions?.length ? "warn" : "ok"} testId="batch-kpi-exceptions" />
                <Kpi label="Status" value={(batchView.batch?.status || "").replace("_", " ")} testId="batch-kpi-status" />
              </div>
            </Section>
            <Section title="Deployment lines" testId="batch-lines">
              <div className="st-panel overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-[color:var(--st-surface-alt)]">
                    <tr>
                      <th className="text-left px-2 py-2">Employee</th>
                      <th className="text-left px-2 py-2">Customer</th>
                      <th className="text-left px-2 py-2">Status</th>
                      <th className="text-left px-2 py-2">Delivery type</th>
                      <th className="text-left px-2 py-2">Required by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(batchView.requests || []).slice(0, 500).map((r) => (
                      <tr key={r.id} className="border-t border-[color:var(--st-border)]" data-testid={`batch-line-${r.id}`}>
                        <td className="px-2 py-1.5">{r.employee_email || "—"}</td>
                        <td className="px-2 py-1.5">{r.customer}</td>
                        <td className="px-2 py-1.5">{r.status}</td>
                        <td className="px-2 py-1.5">{r.delivery_type || "—"}</td>
                        <td className="px-2 py-1.5">{r.required_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}
      </div>
    );
  }

  const rows = draft?.rows || [];
  let filtered = rows;
  if (filter === "delivery") {
    filtered = rows.filter((r) => r._delivery_exception);
  } else if (filter !== "all") {
    filtered = rows.filter((r) => r._status === filter.toUpperCase());
  }
  const c = draft?.counts;

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="bulk-deployment">
      <PageHeader
        eyebrow="Operations · Deploy"
        title="Bulk Deployment"
        context="Onboard 10, 100 or 1,000+ employees in one flow."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="bulk-download-template">
              <Download className="h-4 w-4 mr-1.5" strokeWidth={1.6} /> Download template
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
              onChange={(e) => upload(e.target.files?.[0])} data-testid="bulk-file-input" />
            <Button variant="create" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="bulk-upload">
              <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
              {uploading ? "Parsing…" : "Upload XLSX"}
            </Button>
          </div>
        }
        testId="bulk-header"
      />

      {!draft && (
        <div className="st-panel p-8 text-center">
          <Upload className="h-8 w-8 mx-auto text-[color:var(--st-text-subtle)]" strokeWidth={1.4} />
          <div className="mt-3 text-sm text-[color:var(--st-text-muted)]">
            Download the template, fill one row per employee + primary asset, then upload here.
          </div>
        </div>
      )}

      {draft && (
        <>
          <Section title="Batch summary" testId="bulk-summary">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <Kpi label="Total rows" value={c.total} testId="bulk-kpi-total" />
              <Kpi label="Ready" value={c.ready} tone="ok" testId="bulk-kpi-ready" />
              <Kpi label="Warnings" value={c.warning} tone="warn" testId="bulk-kpi-warn" />
              <Kpi label="Errors" value={c.error} tone="err" testId="bulk-kpi-err" />
              <Kpi label="Standard mismatches" value={draft.override_conflicts} tone={draft.override_conflicts ? "warn" : "ok"} testId="bulk-kpi-conflicts" />
              <Kpi label="Delivery exceptions" value={c.delivery_exceptions || 0} tone={(c.delivery_exceptions || 0) > 0 ? "warn" : "ok"} testId="bulk-kpi-delivery" />
            </div>
            {Object.keys(draft.supply || {}).length > 0 && (
              <div className="mt-4 st-panel p-3 rounded-md">
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)] mb-2">Demand vs supply</div>
                <table className="w-full text-[13px]">
                  <thead className="text-[11px] text-[color:var(--st-text-subtle)]">
                    <tr><th className="text-left">Category</th><th>Required</th><th>Redeploy-ready</th><th>New stock</th><th>Ready to deploy</th><th>Shortfall</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(draft.supply).map(([cat, s]) => (
                      <tr key={cat} className="border-t border-[color:var(--st-border)]">
                        <td>{cat}</td><td className="text-center">{s.required}</td>
                        <td className="text-center">{s.redeployment_ready}</td>
                        <td className="text-center">{s.new_stock}</td>
                        <td className="text-center">{s.ready_to_deploy}</td>
                        <td className={"text-center font-medium " + (s.shortfall ? "text-amber-700" : "text-emerald-700")}>{s.shortfall}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section title={`Rows · ${filtered.length}`} action={
            <div className="flex gap-1 items-center">
              {selected.size > 0 && (
                <button onClick={() => setBulkEditOpen(true)} data-testid="bulk-edit-open"
                  className="px-2 py-1 text-[11px] rounded bg-[color:var(--st-accent)] text-white">
                  Bulk edit {selected.size} row{selected.size === 1 ? "" : "s"}
                </button>
              )}
              {[["all","All"],["ready","Ready"],["warning","Warning"],["error","Error"],["delivery","Delivery exceptions"]].map(([k,l]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`bulk-filter-${k}`}
                  className={"px-2 py-1 text-[11px] rounded " + (filter===k?"bg-[color:var(--st-text)] text-white":"text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)]")}>{l}</button>
              ))}
            </div>
          } testId="bulk-rows">
            <div className="st-panel overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-[color:var(--st-surface-alt)]">
                  <tr>
                    <th className="text-left px-2 py-2">
                      <input type="checkbox" onChange={(e) => {
                        if (e.target.checked) setSelected(new Set(filtered.map((r) => r._row_num)));
                        else setSelected(new Set());
                      }} data-testid="bulk-select-all" />
                    </th>
                    <th className="text-left px-2 py-2">Row</th>
                    <th className="text-left px-2 py-2">Employee</th>
                    <th className="text-left px-2 py-2">Customer</th>
                    <th className="text-left px-2 py-2">Category</th>
                    <th className="text-left px-2 py-2">Qty</th>
                    <th className="text-left px-2 py-2">Status</th>
                    <th className="text-left px-2 py-2">Issues / Inheritance</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 500).map((r) => (
                    <tr key={r._row_num} className="border-t border-[color:var(--st-border)]" data-testid={`bulk-row-${r._row_num}`}>
                      <td className="px-2 py-1.5">
                        <input type="checkbox" checked={selected.has(r._row_num)}
                          onChange={(e) => {
                            const n = new Set(selected);
                            if (e.target.checked) n.add(r._row_num); else n.delete(r._row_num);
                            setSelected(n);
                          }} data-testid={`bulk-select-${r._row_num}`} />
                      </td>
                      <td className="px-2 py-1.5 font-mono">{r._row_num}</td>
                      <td className="px-2 py-1.5">{r["Employee First Name"]} {r["Employee Last Name"]}<div className="text-[10px] text-slate-500">{r["Employee Work Email"]}</div></td>
                      <td className="px-2 py-1.5">{r["Customer"]}</td>
                      <td className="px-2 py-1.5">{r["Primary Asset Category"]}</td>
                      <td className="px-2 py-1.5">{r["Quantity"]}</td>
                      <td className="px-2 py-1.5"><StatusPill s={r._status} /></td>
                      <td className="px-2 py-1.5 text-[11px]">
                        {r._issues?.length > 0 && <div className="text-red-700">{r._issues.join(" · ")}</div>}
                        {r._delivery_issues?.length > 0 && (
                          <div className="text-amber-700" data-testid={`bulk-delivery-issues-${r._row_num}`}>
                            Delivery: {r._delivery_issues.join(" · ")}
                          </div>
                        )}
                        {Object.keys(r._inherited || {}).length > 0 && (
                          <div className="text-blue-700">Inherited: {Object.entries(r._inherited).map(([k,v]) => `${k}=${v}`).join(", ")}</div>
                        )}
                        {Object.keys(r._conflicts || {}).length > 0 && (
                          <div className="text-amber-700">Mismatch: {Object.entries(r._conflicts).map(([k,v]) => `${k}: expected ${v.expected}, uploaded ${v.supplied}`).join("; ")}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDraft(null)}>Discard</Button>
            <Button onClick={confirm} disabled={confirming || c.error > 0} data-testid="bulk-confirm">
              <Rocket className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
              {confirming ? "Creating batch…" : "Confirm & start deployment"}
            </Button>
          </div>
        </>
      )}
      {bulkEditOpen && (
        <BulkEditModal draftId={draft?.id} rowNums={Array.from(selected)}
          onClose={() => setBulkEditOpen(false)}
          onDone={async () => {
            setBulkEditOpen(false); setSelected(new Set());
            try {
              const { data } = await api.get(`/bulk-deployment/drafts/${draft.id}`);
              setDraft(data);
            } catch { /* ignore */ }
          }} />
      )}
    </div>
  );
}

function BulkEditModal({ draftId, rowNums, onClose, onDone }) {
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setFields((f) => v === "" ? (({[k]: _, ...rest}) => rest)(f) : { ...f, [k]: v });
  const submit = async () => {
    if (Object.keys(fields).length === 0) { toast.error("Set at least one field"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/bulk-deployment/drafts/${draftId}/bulk-edit`,
        { row_nums: rowNums, fields });
      toast.success(`Updated ${data.updated} row${data.updated === 1 ? "" : "s"}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk edit failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-edit-modal">
      <div className="st-panel p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="text-[15px] font-heading">Bulk edit · {rowNums.length} row{rowNums.length === 1 ? "" : "s"}</div>
        <div className="mt-1 text-[11px] text-slate-500">
          Address fields are excluded on purpose — bulk edit only touches delivery meta-fields.
        </div>
        <div className="mt-3 space-y-2">
          {[
            ["Delivery Type", "Home / Office / Warehouse Pickup / Temporary"],
            ["Office Location", "e.g. Bangalore Office"],
            ["Deployment Required By", "YYYY-MM-DD"],
            ["Preferred Delivery Window", "e.g. 10:00–18:00"],
            ["Special Instructions", "Applied to every selected row"],
            ["Delivery Country", "ISO country e.g. IN, SG"],
          ].map(([k, hint]) => (
            <div key={k}>
              <label className="text-[11px] text-slate-600">{k}</label>
              <input className="w-full border rounded px-2 py-1 text-[12px]"
                     placeholder={hint} data-testid={`bulk-edit-${k.replace(/\s+/g, '-').toLowerCase()}`}
                     value={fields[k] || ""} onChange={(e) => set(k, e.target.value)} />
            </div>
          ))}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="bulk-edit-submit"
            style={{ background: "var(--st-accent)", color: "white" }}>
            {busy ? "Applying…" : "Apply to selected rows"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone, testId }) {
  const tint = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : tone === "err" ? "text-red-700" : "text-[color:var(--st-text)]";
  return (
    <div className="st-panel p-3" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">{label}</div>
      <div className={"text-2xl font-heading font-semibold mt-1 " + tint}>{value ?? "—"}</div>
    </div>
  );
}
function StatusPill({ s }) {
  const map = { READY: {icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700"},
                WARNING: {icon: AlertTriangle, cls: "bg-amber-50 text-amber-700"},
                ERROR: {icon: XCircle, cls: "bg-red-50 text-red-700"} };
  const v = map[s] || map.READY;
  const Icon = v.icon;
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${v.cls}`}><Icon className="h-2.5 w-2.5" strokeWidth={2}/> {s}</span>;
}
